// chat/actions.ts — the chat control EXECUTOR (fleet App-Brain pattern, adapted to Oriora
// Study's store). The client router (chat/brain.ts) maps ONE user message → ONE ChatAction;
// this file EXECUTES that action against THIS app's on-device store (src/store.ts). Every
// mutating call is LOCAL SQLite over Tauri IPC — no network, no billing.
//
// The ONE AI-billed step reachable from chat ("explain X") reuses the app's EXISTING live
// /explain endpoint via brain.explainConcept — no new/edited server route, still one call.
//
// The action set is DERIVED FROM THIS STORE: subjects (create), sessions (add), artifacts
// (filter/search + delete-with-confirm), and the billed explain.
//
// HITL discipline (fleet standard): destructive actions (delete_artifact / delete_subject)
// are NEVER executed from a chat turn on their own — the Chat UI holds them and requires an
// explicit Confirm tap. This executor just performs the delete once the UI hands it back.

import { store, formatMinutes, parseArtifactBody, todayLocal, KIND_LABELS, type Artifact, type ArtifactKind, type Material, type MaterialSource } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "study-companion";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave, digestMaterial, buildPlan } from "../brain";
import { findNamedMatches } from "./fuzzy";
import { addInstruction } from "./brain";
import { explainConcept, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact artifact row rendered inline in a chat bubble (filter results).
export interface FilterRow {
  id: string;
  title: string;
  kind: ArtifactKind;
  subject: string;
  created: string;
}

// A purely LOCAL filter over the user's own library (no server route — search is computed
// on-device from listArtifacts).
export interface ArtifactFilter {
  /** Free-text contains over title / body text. */
  text?: string;
  kind?: ArtifactKind;
  /** Subject name contains-match. */
  subject?: string;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_subject"; name: string }
  | { kind: "log_session"; subjectTerm: string; minutes: number; note?: string }
  | { kind: "filter"; filter: ArtifactFilter; label?: string }
  | { kind: "delete_artifact"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "explain"; concept: string } // the ONE billed step (EXISTING /explain endpoint)
  // G3: the flagship features the chat could not previously run.
  | { kind: "digest"; subjectId: string; text: string; title?: string; materialId?: string; source?: MaterialSource } // billed (/digest)
  | { kind: "plan"; subjectId: string; goal: string; deadline?: string; topics?: string; hoursPerWeek?: number }      // billed (/plan)
  | { kind: "add_material"; subjectId: string; subjectName: string; title: string; text: string; source?: MaterialSource }
  | { kind: "rename_subject"; id: string; from: string; to: string }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;   // shown back in the chat
  changed?: boolean; // the library changed → the main screen should refresh
  rows?: FilterRow[]; // filter matches to render inline
}

const norm = (s: string) => s.trim().toLowerCase();

/** Apply an ArtifactFilter locally — every condition ANDs; empty filter matches all. */
export function applyArtifactFilter(
  artifacts: Artifact[],
  f: ArtifactFilter,
  subjectName: (id: string) => string,
): Artifact[] {
  return artifacts.filter((a) => {
    if (f.kind && a.kind !== f.kind) return false;
    if (f.subject && !norm(subjectName(a.subject_id)).includes(norm(f.subject))) return false;
    if (f.text) {
      const t = norm(f.text);
      const hit = a.title.toLowerCase().includes(t) || a.body.toLowerCase().includes(t);
      if (!hit) return false;
    }
    return true;
  });
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_subject": {
        const s = await store.createSubject(a.name);
        return { ok: true, message: `Added the subject "${s.name}".`, changed: true };
      }

      case "log_session": {
        const subjects = await store.listSubjects();
        const term = norm(a.subjectTerm);
        const hits = subjects.filter((s) => norm(s.name).includes(term));
        if (hits.length === 0) return { ok: false, message: `I couldn't find a subject matching "${a.subjectTerm}". Add it first: "add subject ${a.subjectTerm}".` };
        if (hits.length > 1) return { ok: false, message: `I found ${hits.length} subjects matching "${a.subjectTerm}" — be more specific: ${hits.slice(0, 5).map((s) => s.name).join(", ")}.` };
        const sess = await store.addSession({ subject_id: hits[0].id, minutes: a.minutes, note: a.note ?? "" });
        return { ok: true, message: `Logged ${formatMinutes(sess.minutes)} of ${hits[0].name} for ${sess.studied_on}.`, changed: true };
      }

      case "filter": {
        const [all, subjects] = await Promise.all([store.listArtifacts(), store.listSubjects()]);
        const nameOf = (id: string) => subjects.find((s) => s.id === id)?.name ?? "";
        const matches = applyArtifactFilter(all, a.filter, nameOf);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        const rows: FilterRow[] = matches.slice(0, 50).map((x) => ({
          id: x.id,
          title: x.title,
          kind: x.kind,
          subject: nameOf(x.subject_id),
          created: x.created_at.slice(0, 10),
        }));
        return {
          ok: true,
          message: `${matches.length} ${matches.length === 1 ? "entry" : "entries"}${forLabel}` + (matches.length > 50 ? " (showing first 50)" : ""),
          rows,
        };
      }

      case "delete_artifact":
        await store.deleteArtifact(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the entry"}.`, changed: true };

      case "explain": {
        // The ONE billed step — reuses the app's EXISTING live /explain endpoint. The
        // study-tool fence runs inside explainConcept (pre-LLM) AND server-side. Errors
        // (BrainError) are caught below and shown in the chat.
        const r = await explainConcept(a.concept, "new");
        if (r.refusal) return { ok: false, message: r.refusal };
        const text = [
          r.analogy ? `Think of it like this: ${r.analogy}` : "",
          r.definition ? `Precisely: ${r.definition}` : "",
          r.worked_example ? `Worked example: ${r.worked_example}` : "",
          r.misconception ? `Watch out: ${r.misconception}` : "",
        ].filter(Boolean).join("\n\n");
        return { ok: true, message: text || "Nothing came back — try naming the concept differently." };
      }

      // ── G3: the flagship features, now runnable from the chat ──────────────
      case "digest": {
        // ONE billed call (/digest). The essay-mill fence runs inside digestMaterial (pre-LLM) too.
        // Mirrors the UI flow exactly: save the material, then the notes artifact against it,
        // so a chat-made digest shows up in the library the same as a screen-made one.
        const r = await digestMaterial(a.text, { title: a.title });
        if (r.refusal) return { ok: false, message: r.refusal };
        const title = a.title?.trim() || (r.summary ?? "Study notes").slice(0, 60);
        let materialId = a.materialId ?? "";
        if (!materialId) {
          const m = await store.createMaterial({ subject_id: a.subjectId, title, source: a.source ?? "typed", content: a.text });
          materialId = m.id;
        }
        await store.createArtifact({
          subject_id: a.subjectId, material_id: materialId, kind: "notes", title,
          body: JSON.stringify({ key_concepts: r.key_concepts, summary: r.summary, self_check: r.self_check }),
        });
        const concepts = (r.key_concepts ?? []).slice(0, 3).join(", ");
        return {
          ok: true, changed: true,
          message: `Made study notes from “${title}” — saved to your library.` + (concepts ? `\nKey concepts: ${concepts}.` : ""),
        };
      }

      case "plan": {
        if (!a.deadline || !/^\d{4}-\d{2}-\d{2}$/.test(a.deadline)) {
          return { ok: false, message: "When's the deadline? Tell me the date and I'll build the weeks around it." };
        }
        const r = await buildPlan({
          goal: a.goal,
          deadline: a.deadline,
          weeklyHours: a.hoursPerWeek ?? 5,
          topics: a.topics ? a.topics.split(",").map((t) => t.trim()).filter(Boolean) : [],
          today: todayLocal(),
        });
        if (r.refusal) return { ok: false, message: r.refusal };
        await store.createArtifact({
          subject_id: a.subjectId, kind: "plan", title: r.goal || a.goal,
          body: JSON.stringify({ goal: r.goal, weeks: r.weeks, note: r.note }),
        });
        const weeks = r.weeks?.length ?? 0;
        return { ok: true, changed: true, message: `Built a ${weeks}-week plan for “${a.goal}” — saved to your library.` };
      }

      case "add_material": {
        // Free, on-device. A photographed page arrives here as its OCR'd TEXT — the image never left the phone.
        await store.createMaterial({ subject_id: a.subjectId, title: a.title, source: a.source ?? "typed", content: a.text });
        return { ok: true, changed: true, message: `Saved “${a.title}” to ${a.subjectName}. Say “digest ${a.title}” and I'll turn it into notes.` };
      }

      case "rename_subject": {
        await store.renameSubject(a.id, a.to);
        return { ok: true, changed: true, message: `Renamed ${a.from} to ${a.to}.` };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my library" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_to_cloud": {
        // Saves the CURRENT conversation (or a note) to the customer's OWN cloud — distinct from
        // backup_save (the whole library). Text relay only; Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}

/** Short label for a library row shown in chat. */
export function rowLabel(r: FilterRow): string {
  return `${KIND_LABELS[r.kind]} · ${r.subject || "—"}`;
}

/** Find artifacts by title (Pattern 6 — misspelling-tolerant, so "Ch 4 nots" still resolves). */
export async function findArtifactsByTitle(term: string): Promise<Artifact[]> {
  if (!term.trim()) return [];
  const all = await store.listArtifacts();
  const hits = findNamedMatches(all.map((a) => ({ name: a.title, a })), term);
  return hits.map((h) => h.a);
}

/** Find saved materials by title, same fuzzy rules — used by the chat's `digest` action. */
export async function findMaterialsByTitle(term: string): Promise<Material[]> {
  if (!term.trim()) return [];
  const all = await store.listMaterials();
  const hits = findNamedMatches(all.map((m) => ({ name: m.title, m })), term);
  return hits.map((h) => h.m);
}

// Re-export for the Chat UI's inline rendering needs.
export { parseArtifactBody };
